import { useEffect, useState, type ReactNode } from 'react';
import type { DescargaCopia, EnlaceBaja, EnlacePermiso, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Deshacer } from '../componentes/Deshacer';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { useEnvioRetrasado } from './envioRetrasado';
import { esCodigo, fechaCorta, hora, iconoCanal, nombreCanal, textoDeError } from './logica';
import { AvisoPc, Baldosa, BotonPc, Pagina } from './Pagina';
import { D, T } from './textos';

/**
 * 2.11 Enlaces de un toque (sin identificarse): baja de un canal (T36, T37), confirmar un permiso comercial dado en persona
 * (T38, G155, G162) y descargar la copia de datos (T39, G156). **Abrir un enlace nunca escribe nada**: la baja, el permiso y
 * la descarga solo ocurren al pulsar. La baja lleva «Deshacer» 5 s (T37, G240): no se envía hasta que pasan.
 */
interface PropsEnlace {
    ops: OperacionesClienteFinal;
    token: string;
    irAMisDatos: () => void;
}

interface Tarjeta {
    baldosa: [NombreIcono, string, string];
    chip?: [string, EstadoChip];
    titulo: string;
    sub: string;
    lineas?: readonly [NombreIcono, string][];
    acciones?: ReactNode;
    nota?: [NombreIcono, string];
    error?: string | null;
}

function TarjetaToque({ baldosa, chip, titulo, sub, lineas, acciones, nota, error = null }: Tarjeta) {
    return (
        <section className="rv-pc-card" aria-labelledby="rv-pc-toque-t">
            <div className="rv-pc-baldosa-fila">
                <Baldosa icono={baldosa[0]} fondo={baldosa[1]} color={baldosa[2]} />
                {chip !== undefined && <Chip estado={chip[1]}>{chip[0]}</Chip>}
            </div>
            <div className="rv-pc-card__cab">
                <h1 id="rv-pc-toque-t">{titulo}</h1>
                <span>{sub}</span>
            </div>
            {lineas !== undefined && lineas.length > 0 && (
                <div className="rv-pc-lineas">
                    {lineas.map(([icono, texto]) => (
                        <div key={texto} className="rv-pc-linea">
                            <Icono nombre={icono} />
                            <span>{texto}</span>
                        </div>
                    ))}
                </div>
            )}
            {error !== null && <AvisoPc texto={error} />}
            {acciones !== undefined && <div className="rv-pc-acciones">{acciones}</div>}
            {nota !== undefined && (
                <div className="rv-pc-nota">
                    <Icono nombre={nota[0]} />
                    <span>{nota[1]}</span>
                </div>
            )}
        </section>
    );
}

const NEGRA = 'var(--rv-ink)';
const ROJA = 'var(--rv-red)';
const PAPEL = 'var(--rv-paper)';

function Cargando({ negocio }: { negocio: boolean }) {
    return (
        <section className="rv-pc-card" aria-busy="true" aria-label={D.cargando}>
            <span className="rv-pc-esqueleto" style={{ width: 56, height: 56, borderRadius: 16 }} />
            <span className="rv-pc-esqueleto" style={{ width: '80%', height: 30 }} />
            <span className="rv-pc-esqueleto" style={{ width: negocio ? '95%' : '70%', height: 16 }} />
        </section>
    );
}

/** Página de un negocio mientras se lee el enlace (sin su nombre todavía): el pie de la cuenta. */
function PaginaDelEnlace({ negocio, children }: { negocio: string | null; children: ReactNode }) {
    return (
        <Pagina
            cabecera={negocio === null ? { tipo: 'cuenta' } : { tipo: 'negocio', nombre: negocio }}
            // «Privacidad de [negocio]»: los enlaces aún no traen `privacy_url` (pregunta abierta), así que va sin dirección.
            pie={negocio === null ? { tipo: 'cuenta' } : { tipo: 'negocio', nombre: negocio, privacidad: null }}
            centrada
        >
            {children}
        </Pagina>
    );
}

type EstadoBaja = { e: 'cargando' } | { e: 'caducado' } | { e: 'pregunta'; info: EnlaceBaja } | { e: 'hecha'; info: EnlaceBaja; enviada: boolean } | { e: 'ya'; info: EnlaceBaja };

export function PaginaBaja({ ops, token, irAMisDatos, esperaDeshacer = 5000 }: PropsEnlace & { esperaDeshacer?: number }) {
    const [estado, setEstado] = useState<EstadoBaja>({ e: 'cargando' });
    const [error, setError] = useState<string | null>(null);
    const envio = useEnvioRetrasado(esperaDeshacer);

    useEffect(() => {
        let vivo = true;
        ops.getUnsubscribeLink(token)
            .then((info) => {
                if (vivo) setEstado(info.already_unsubscribed === true ? { e: 'ya', info } : { e: 'pregunta', info });
            })
            .catch(() => {
                if (vivo) setEstado({ e: 'caducado' });
            });
        return () => {
            vivo = false;
        };
    }, [ops, token]);

    const darseDeBaja = (info: EnlaceBaja) => {
        setError(null);
        setEstado({ e: 'hecha', info, enviada: false });
        envio.programar(
            'baja',
            async () => {
                try {
                    await ops.useUnsubscribeLink(token);
                    setEstado({ e: 'hecha', info, enviada: true });
                } catch (e) {
                    if (esCodigo(e, 'not_found')) setEstado({ e: 'caducado' });
                    else if (esCodigo(e, 'conflict')) setEstado({ e: 'ya', info });
                    else {
                        setEstado({ e: 'pregunta', info });
                        setError(textoDeError(e));
                    }
                }
            },
            () => {
                setEstado({ e: 'pregunta', info });
            },
        );
    };

    const elegir = <BotonPc tipo="sec" texto={T.t36.elegir} icono="sliders-horizontal" onClick={irAMisDatos} />;
    const negocio = estado.e === 'cargando' || estado.e === 'caducado' ? null : estado.info.business_name;
    const canal = estado.e === 'cargando' || estado.e === 'caducado' ? 'email' : estado.info.channel;
    let tarjeta: ReactNode;
    switch (estado.e) {
        case 'cargando':
            tarjeta = <Cargando negocio />;
            break;
        case 'caducado':
            tarjeta = (
                <TarjetaToque
                    baldosa={['link', PAPEL, NEGRA]}
                    chip={[D.enlaceNoValido, 'problem']}
                    titulo={T.t37.noValidoTitulo}
                    sub={T.t37.noValidoSub}
                    acciones={<BotonPc texto={D.irAMisDatos} icono="arrow-right" onClick={irAMisDatos} />}
                />
            );
            break;
        case 'pregunta':
            tarjeta = (
                <TarjetaToque
                    baldosa={[iconoCanal(canal), NEGRA, '#fff']}
                    titulo={T.t36.titulo(estado.info.business_name, nombreCanal(canal))}
                    sub={T.t36.sub}
                    error={error}
                    acciones={
                        <>
                            <BotonPc texto={T.t36.baja} icono="ban" onClick={() => { darseDeBaja(estado.info); }} />
                            {elegir}
                        </>
                    }
                />
            );
            break;
        case 'hecha': {
            const esperando = envio.pendiente === 'baja';
            tarjeta = (
                <TarjetaToque
                    baldosa={['check', ROJA, '#fff']}
                    chip={[D.bajaHecha, 'confirmed']}
                    titulo={T.t37.titulo(estado.info.business_name, nombreCanal(canal))}
                    sub={T.t37.sub}
                    acciones={esperando ? <Deshacer onDeshacer={envio.deshacer} duracion={esperaDeshacer} ancho="100%" grande /> : elegir}
                    nota={esperando ? undefined : ['info', T.t37.nota]}
                />
            );
            break;
        }
        case 'ya':
            tarjeta = (
                <TarjetaToque
                    baldosa={[iconoCanal(canal), NEGRA, '#fff']}
                    chip={[D.yaEstabas, 'neutral']}
                    titulo={T.t37.yaTitulo(estado.info.business_name, nombreCanal(canal))}
                    sub={T.t37.yaSub}
                    acciones={elegir}
                />
            );
            break;
    }
    return <PaginaDelEnlace negocio={negocio}>{tarjeta}</PaginaDelEnlace>;
}

type EstadoPermiso = { e: 'cargando' } | { e: 'caducado' } | { e: 'pregunta'; info: EnlacePermiso } | { e: 'aceptado'; info: EnlacePermiso } | { e: 'rechazado'; info: EnlacePermiso };

/** Fecha en que lo pidió en el local: el enlace caduca a los 7 días de enviarse (G162; el enlace no trae la fecha, pregunta abierta). */
const fechaDelPermiso = (caduca: string): string => fechaCorta(new Date(Date.parse(caduca) - 7 * 24 * 3600 * 1000).toISOString());

export function PaginaPermiso({ ops, token, irAMisDatos }: PropsEnlace) {
    const [estado, setEstado] = useState<EstadoPermiso>({ e: 'cargando' });
    const [ocupado, setOcupado] = useState<'accept' | 'decline' | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        ops.getConsentConfirmationLink(token)
            .then((info) => {
                if (!vivo) return;
                setEstado(info.status === 'confirmed' ? { e: 'aceptado', info } : info.status === 'declined' ? { e: 'rechazado', info } : { e: 'pregunta', info });
            })
            .catch(() => {
                if (vivo) setEstado({ e: 'caducado' });
            });
        return () => {
            vivo = false;
        };
    }, [ops, token]);

    const decidir = async (info: EnlacePermiso, decision: 'accept' | 'decline') => {
        setOcupado(decision);
        setError(null);
        try {
            await ops.confirmConsentByLink(token, decision);
            setEstado(decision === 'accept' ? { e: 'aceptado', info } : { e: 'rechazado', info });
        } catch (e) {
            if (esCodigo(e, 'not_found') || esCodigo(e, 'conflict')) setEstado({ e: 'caducado' });
            else setError(textoDeError(e));
        } finally {
            setOcupado(null);
        }
    };

    const negocio = estado.e === 'cargando' || estado.e === 'caducado' ? null : estado.info.business_name;
    let tarjeta: ReactNode;
    switch (estado.e) {
        case 'cargando':
            tarjeta = <Cargando negocio />;
            break;
        case 'caducado':
            tarjeta = (
                <TarjetaToque
                    baldosa={['link', PAPEL, NEGRA]}
                    chip={[D.caducadoChip, 'problem']}
                    titulo={T.t38.caducadoTitulo}
                    sub={T.t38.caducadoSub}
                    acciones={<BotonPc texto={D.irAMisDatos} icono="arrow-right" onClick={irAMisDatos} />}
                />
            );
            break;
        case 'pregunta': {
            const { info } = estado;
            const canal = nombreCanal(info.channel);
            tarjeta = (
                <TarjetaToque
                    baldosa={[iconoCanal(info.channel), NEGRA, '#fff']}
                    chip={[D.pendienteConfirmar, 'pending']}
                    titulo={T.t38.titulo(info.business_name, canal)}
                    sub={T.t38.sub(info.business_name, fechaDelPermiso(info.expires_at), canal)}
                    lineas={[
                        ['megaphone', D.ofertasYNovedades],
                        [iconoCanal(info.channel), D.por(canal)],
                        ['clock', T.t38.caduca],
                    ]}
                    error={error}
                    acciones={
                        <>
                            <BotonPc texto={T.t38.si} icono="check" ocupado={ocupado === 'accept'} ocupadoTexto={D.confirmando} desactivado={ocupado === 'decline'} onClick={() => void decidir(info, 'accept')} />
                            <BotonPc tipo="sec" texto={T.t38.no} icono="x" ocupado={ocupado === 'decline'} ocupadoTexto={D.guardando} desactivado={ocupado === 'accept'} onClick={() => void decidir(info, 'decline')} />
                        </>
                    }
                />
            );
            break;
        }
        case 'aceptado':
            tarjeta = (
                <TarjetaToque baldosa={['check', ROJA, '#fff']} chip={[D.confirmado, 'confirmed']} titulo={T.t38.aceptadoTitulo(nombreCanal(estado.info.channel))} sub={T.t38.aceptadoSub} />
            );
            break;
        case 'rechazado':
            tarjeta = (
                <TarjetaToque baldosa={[iconoCanal(estado.info.channel), NEGRA, '#fff']} chip={[D.sinOfertasChip, 'neutral']} titulo={T.t38.rechazadoTitulo} sub={T.t38.rechazadoSub(estado.info.business_name)} />
            );
            break;
    }
    return <PaginaDelEnlace negocio={negocio}>{tarjeta}</PaginaDelEnlace>;
}

type EstadoCopia = { e: 'lista' } | { e: 'caducado' } | { e: 'descargada'; descarga: DescargaCopia };

/** Abre la dirección temporal del archivo (no es la API: es el archivo, válido 15 minutos). */
const abrirEnElNavegador = (url: string) => {
    window.location.assign(url);
};

export function PaginaCopia({ ops, token, irAMisDatos, abrir = abrirEnElNavegador }: PropsEnlace & { abrir?: (url: string) => void }) {
    const [estado, setEstado] = useState<EstadoCopia>({ e: 'lista' });
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Solo al pulsar (G156): abrir la página no gasta el enlace.
    const descargar = async () => {
        setOcupado(true);
        setError(null);
        try {
            const descarga = await ops.downloadPrivacyExport(token);
            abrir(descarga.download_url);
            setEstado({ e: 'descargada', descarga });
        } catch (e) {
            if (esCodigo(e, 'not_found')) setEstado({ e: 'caducado' });
            else setError(textoDeError(e));
        } finally {
            setOcupado(false);
        }
    };

    let tarjeta: ReactNode;
    switch (estado.e) {
        case 'lista':
            tarjeta = (
                <TarjetaToque
                    baldosa={['download', NEGRA, '#fff']}
                    chip={[D.lista, 'confirmed']}
                    titulo={T.t39.titulo}
                    sub={T.t39.sub}
                    lineas={[['clock', T.t39.caduca]]}
                    error={error}
                    acciones={<BotonPc texto={T.t39.descargar} icono="download" ocupado={ocupado} ocupadoTexto={D.preparando} onClick={() => void descargar()} />}
                />
            );
            break;
        case 'caducado':
            tarjeta = (
                <TarjetaToque
                    baldosa={['link', PAPEL, NEGRA]}
                    chip={[D.caducadoChip, 'problem']}
                    titulo={T.t39.caducadoTitulo}
                    sub={T.t39.caducadoSub}
                    acciones={<BotonPc texto={D.irAMisDatos} icono="arrow-right" onClick={irAMisDatos} />}
                />
            );
            break;
        case 'descargada': {
            const { descarga } = estado;
            tarjeta = (
                <TarjetaToque
                    baldosa={['check', ROJA, '#fff']}
                    chip={[D.descargada, 'confirmed']}
                    titulo={D.descargaEmpezada}
                    sub={D.descargaSub(hora(descarga.expires_at))}
                    acciones={
                        <BotonPc
                            tipo="sec"
                            texto={D.descargarOtraVez}
                            icono="download"
                            onClick={() => {
                                // La dirección del archivo dura 15 minutos; pasados, el enlace ya está gastado.
                                if (Date.parse(descarga.expires_at) > Date.now()) abrir(descarga.download_url);
                                else setEstado({ e: 'caducado' });
                            }}
                        />
                    }
                    nota={['lock', T.t39.guardala]}
                />
            );
            break;
        }
    }
    return (
        <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} centrada>
            {tarjeta}
        </Pagina>
    );
}
